import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {API_BASE, parseJwtPayload, setCookie} from '../api';

const IDENTITY_URL = 'https://identity.intechweben.com/api/v1';

function getJwtToken() {
  const match = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('jwtToken='));
  return match ? match.split('=')[1] : null;
}

function getDisplayName() {
  const match = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('displayName='));
  return match ? decodeURIComponent(match.split('=')[1]) : null;
}

function getTokenUsername() {
  const token = getJwtToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.username || payload.email || payload.sub || null;
  } catch { return null; }
}

function getRenewalToken() {
  const match = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('renewalToken='));
  return match ? match.split('=')[1] : null;
}

function clearAuth() {
  document.cookie = 'jwtToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = 'renewalToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = 'displayName=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = 'tenantId=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = 'tenantName=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
}

export default function AcceptInvite() {
  const {token} = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({password: '', confirmPassword: '', firstName: '', lastName: '', dateOfBirth: ''});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [loggedIn, setLoggedIn] = useState(!!getJwtToken());
  const [displayName] = useState(getDisplayName());

  useEffect(() => {
    fetch(`${API_BASE}/invites/accept/${token}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        setInvite(data);
        if (data && getJwtToken()) {
          const username = getTokenUsername();
          if (username && username.toLowerCase() !== data.email.toLowerCase()) {
            clearAuth();
            setLoggedIn(false);
          }
        }
        if (data && !getJwtToken()) setMode(data.accountExists ? 'login' : 'register');
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token]);

  const set = (field) => (e) => setForm(f => ({...f, [field]: e.target.value}));

  const refreshSession = async () => {
    const renewal = getRenewalToken();
    if (!renewal) return;
    try {
      const res = await fetch(`${IDENTITY_URL}/token/renew`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({renewalToken: renewal}),
      });
      if (!res.ok) return;
      const data = await res.json();
      const payload = parseJwtPayload(data.renewalToken);
      const expires = payload?.exp ? new Date(payload.exp * 1000) : null;
      setCookie('jwtToken', data.jwtToken, expires);
      setCookie('renewalToken', data.renewalToken, expires);
    } catch { /* ignore */ }
  };

  const acceptWithToken = async (jwtToken) => {
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/invites/accept/${token}`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({jwtToken}),
      });
      if (!res.ok) {
        setError('Failed to accept invite. Please try again.');
        setSubmitting(false);
        return;
      }
      
      const inviteDetail = await res.json();
      
      // Use the fresh JWT token from the response if available
      const renewal = getRenewalToken();
      const rPayload = renewal ? parseJwtPayload(renewal) : null;
      const expires = rPayload?.exp ? new Date(rPayload.exp * 1000) : null;
      if (inviteDetail.freshJwtToken) {
        setCookie('jwtToken', inviteDetail.freshJwtToken, expires);
      } else {
        if (renewal) {
          try {
            const renewRes = await fetch(`${IDENTITY_URL}/token/renew`, {
              method: 'POST',
              headers: {'Content-Type': 'application/json'},
              body: JSON.stringify({renewalToken: renewal}),
            });
            if (renewRes.ok) {
              const renewData = await renewRes.json();
              const newPayload = parseJwtPayload(renewData.renewalToken);
              const newExpires = newPayload?.exp ? new Date(newPayload.exp * 1000) : null;
              setCookie('jwtToken', renewData.jwtToken, newExpires);
              setCookie('renewalToken', renewData.renewalToken, newExpires);
            }
          } catch (error) {
            console.warn('Token renewal failed:', error);
          }
        }
      }
      
      navigate('/tenants');
    } catch {
      setError('Unable to connect to service');
      setSubmitting(false);
    }
  };

  const loginAndAccept = async (email, password) => {
    const loginRes = await fetch(`${IDENTITY_URL}/login`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({username: email, password, relm: 'matterking.com'}),
    });
    if (!loginRes.ok) return null;
    const data = await loginRes.json();
    const payload = parseJwtPayload(data.renewalToken);
    const expires = payload?.exp ? new Date(payload.exp * 1000) : null;
    setCookie('jwtToken', data.jwtToken, expires);
    setCookie('renewalToken', data.renewalToken, expires);
    setCookie('displayName', encodeURIComponent(data.displayName || ''), expires);
    return data.jwtToken;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const jwtToken = await loginAndAccept(invite.email, form.password);
      if (!jwtToken) { setError('Invalid password'); setSubmitting(false); return; }
      await acceptWithToken(jwtToken);
    } catch { setError('Unable to connect to login service'); setSubmitting(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      const regRes = await fetch(`${IDENTITY_URL}/user`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({username: invite.email, password: form.password, firstName: form.firstName, lastName: form.lastName, dateOfBirth: form.dateOfBirth, relm: 'matterking.com'}),
      });
      if (!regRes.ok) {
        const body = await regRes.text();
        setError(body || 'Registration failed');
        setSubmitting(false);
        return;
      }
      const jwtToken = await loginAndAccept(invite.email, form.password);
      if (!jwtToken) { setError('Account created but login failed. Try signing in.'); setSubmitting(false); return; }
      await acceptWithToken(jwtToken);
    } catch { setError('Unable to connect to service'); setSubmitting(false); }
  };

  const handleAcceptAsCurrentUser = () => acceptWithToken(getJwtToken());

  const handleSwitchAccount = () => {
    clearAuth();
    setLoggedIn(false);
    if (invite.accountExists) setMode('login');
    else setMode('register');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><p>Loading...</p></div>;

  if (!invite) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Invite Not Found</h1>
        <p className="text-gray-600">This invite link is invalid, expired, or has already been used.</p>
      </div>
    </div>
  );

  if (getJwtToken() && submitting) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
        <p className="text-gray-600">Accepting invite...</p>
      </div>
    </div>
  );

  if (loggedIn) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-lg">
        <div className="text-center mb-6">
          <img src="/images/matterking-logo.png" alt="Matter King" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">You've been invited!</h1>
          <p className="text-gray-600 mt-2">Join <span className="font-semibold">{invite.tenantName}</span> on Matter King</p>
        </div>
        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
        <div className="bg-gray-50 rounded-lg p-4 mb-4 text-center">
          <p className="text-sm text-gray-500">Signed in as</p>
          <p className="font-semibold text-gray-900">{displayName || 'Current User'}</p>
        </div>
        <button onClick={handleAcceptAsCurrentUser} disabled={submitting}
          className="w-full bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 mb-3">
          {submitting ? 'Joining...' : 'Accept & Join'}
        </button>
        <button onClick={handleSwitchAccount}
          className="w-full text-sm text-gray-500 hover:text-gray-700">
          Use a different account
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-lg">
        <div className="text-center mb-6">
          <img src="/images/matterking-logo.png" alt="Matter King" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">You've been invited!</h1>
          <p className="text-gray-600 mt-2">Join <span className="font-semibold">{invite.tenantName}</span> on Matter King</p>
          <p className="text-sm text-gray-400 mt-1">{invite.email}</p>
        </div>

        <div className="flex mb-6 border-b">
          <button onClick={() => setMode('login')}
            className={`flex-1 pb-3 text-sm font-medium ${mode === 'login' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500'}`}>
            I have an account
          </button>
          <button onClick={() => setMode('register')}
            className={`flex-1 pb-3 text-sm font-medium ${mode === 'register' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500'}`}>
            I'm new here
          </button>
        </div>

        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

        {mode === 'login' ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input type="password" required value={form.password} onChange={set('password')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
            </div>
            <button type="submit" disabled={submitting}
              className="w-full bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 font-medium disabled:opacity-50">
              {submitting ? 'Joining...' : 'Sign In & Join'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input type="text" required value={form.firstName} onChange={set('firstName')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input type="text" required value={form.lastName} onChange={set('lastName')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <input type="date" required value={form.dateOfBirth} onChange={set('dateOfBirth')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input type="password" required value={form.password} onChange={set('password')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input type="password" required value={form.confirmPassword} onChange={set('confirmPassword')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
            </div>
            <button type="submit" disabled={submitting}
              className="w-full bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 font-medium disabled:opacity-50">
              {submitting ? 'Creating...' : 'Create Account & Join'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
